import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Calendar } from 'lucide-react'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import autoIncrementService from '../../services/autoIncrementService'

const AutoIncrementMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [sequenceData, setSequenceData] = useState(null)
  const [formData, setFormData] = useState({
    sequenceName: '',
    description: '',
    currentValue: 1,
    prefix: '',
    digits: 4,
    incrementBy: 1,
    status: 'Active',
    createdDate: '24-09-2025'
  })

  // Load sequence data from location state or fetch from API
  useEffect(() => {
    if (location.state?.sequenceData) {
      console.log('Received sequence data:', location.state.sequenceData)
      console.log('Sequence ID:', location.state.sequenceData.id)
      console.log('Sequence _id:', location.state.sequenceData._id)
      
      // Ensure we have an id field (either id or _id)
      const sequenceWithId = {
        ...location.state.sequenceData,
        id: location.state.sequenceData.id || location.state.sequenceData._id
      }
      
      setSequenceData(sequenceWithId)
      setFormData({
        sequenceName: sequenceWithId.sequenceName || '',
        description: sequenceWithId.description || '',
        currentValue: sequenceWithId.currentValue || 1,
        prefix: sequenceWithId.prefix || '',
        digits: sequenceWithId.digits || 4,
        incrementBy: sequenceWithId.incrementBy || 1,
        status: sequenceWithId.status || 'Active',
        createdDate: sequenceWithId.createdDate || '24-09-2025'
      })
    } else {
      // If no state data, redirect back to listing
      toast.error('No sequence data found')
      navigate('/auto-increment-master')
    }
  }, [location.state, navigate])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    // Validate required fields
    if (!formData.sequenceName.trim()) {
      toast.error('Sequence Name is required')
      return
    }
    if (!formData.description.trim()) {
      toast.error('Description is required')
      return
    }
    if (formData.currentValue < 1) {
      toast.error('Current Value must be at least 1')
      return
    }
    if (formData.incrementBy < 1) {
      toast.error('Increment By must be at least 1')
      return
    }
    if (formData.digits < 1 || formData.digits > 10) {
      toast.error('Digits must be between 1 and 10')
      return
    }

    // Check if sequenceData and id exist
    if (!sequenceData || !sequenceData.id) {
      toast.error('Sequence data not found. Please go back and try again.')
      return
    }

    try {
      toast.info('Updating sequence...')
      console.log('Updating sequence with ID:', sequenceData.id)
      const response = await autoIncrementService.updateSequence(sequenceData.id, formData)
      if (response.success) {
        toast.success('Auto increment sequence updated successfully!', { autoClose: 2000 })
        navigate('/settings/auto-increment-master')
      } else {
        toast.error(response.message || 'Failed to update sequence')
      }
    } catch (error) {
      console.error('Error updating sequence:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 404) {
        toast.error('Sequence not found')
      } else if (error.response?.status === 409) {
        toast.error('Sequence name already exists in this company')
      } else {
        toast.error('Failed to update sequence. Please try again.')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      sequenceName: sequenceData.sequenceName || '',
      description: sequenceData.description || '',
      currentValue: sequenceData.currentValue || 1,
      prefix: sequenceData.prefix || '',
      digits: sequenceData.digits || 4,
      incrementBy: sequenceData.incrementBy || 1,
      status: sequenceData.status || 'Active',
      createdDate: sequenceData.createdDate || '24-09-2025'
    })
    toast.info('Form reset to original values')
  }

  const handleBack = () => {
    navigate('/settings/auto-increment-master')
  }

  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Transactions',
      path: '/settings?tab=transactions',
      onClick: () => navigate('/settings?tab=transactions')
    },
    {
      label: 'Auto Increment Master',
      path: '/settings/auto-increment-master',
      onClick: () => navigate('/settings/auto-increment-master')
    },
    {
      label: 'Edit',
      path: '/settings/auto-increment-master/edit',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Auto Increment Master - Edit</h1>
          <p>Edit auto-increment sequence configuration</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Auto Increment Master - Edit</h2>
        </div>
          
        <form className="form-content">
          {/* Row 1: Sequence Name, Description, Current Value, Increment By */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="sequenceName" className="form-label required">
                Sequence Name
              </label>
              <input
                type="text"
                id="sequenceName"
                name="sequenceName"
                value={formData.sequenceName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter sequence name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label required">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter sequence description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentValue" className="form-label required">
                Current Value
              </label>
              <input
                type="number"
                id="currentValue"
                name="currentValue"
                value={formData.currentValue}
                onChange={handleInputChange}
                className="form-input"
                min="1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="incrementBy" className="form-label required">
                Increment By
              </label>
              <input
                type="number"
                id="incrementBy"
                name="incrementBy"
                value={formData.incrementBy}
                onChange={handleInputChange}
                className="form-input"
                min="1"
              />
            </div>
          </div>

          {/* Row 2: Prefix, Digits, Status, Created Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="prefix" className="form-label">
                Prefix
              </label>
              <input
                type="text"
                id="prefix"
                name="prefix"
                value={formData.prefix}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter prefix (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="digits" className="form-label">
                Digits
              </label>
              <input
                type="number"
                id="digits"
                name="digits"
                value={formData.digits}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter number of digits"
                min="1"
                max="10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="createdDate" className="form-label required">
                Created Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="createdDate"
                  name="createdDate"
                  value={formData.createdDate}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="DD-MM-YYYY"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AutoIncrementMasterEdit